/**
 * UI Operations Action Definitions
 *
 * Every user-facing BMaps operation that a chemist might ask for in
 * natural language. These are registered so the LLM can see them in
 * the action registry and choose the right one when a user says
 * "open the sketcher", "undo that", "hide this compound", etc.
 *
 * **Architecture principle:** if a human hearing the request would
 * know what BMaps operation to perform, the LLM should too. Every
 * UserCmd that maps to a user-visible operation gets a registry
 * entry here. Internal plumbing (DumpWorkspace, Redisplay, etc.)
 * does NOT get registered.
 *
 * **Security boundary:** none of these actions expose compound
 * identity (names, SMILES, structures). They change views, open
 * panels, navigate compounds by index, and toggle display settings.
 *
 * @module gibbs/registry/actions/ui-operations
 */

import { param, pre } from '../schema';

// ─── Display Settings ──────────────────────────────────────────────────────

export const setDisplayStyle = {
    id: 'set-display-style',
    name: 'Change Atom Rendering Style',
    category: 'Display',
    description: 'Change how atoms are RENDERED — wireframe, sticks, ball-and-stick, spacefill, cartoon, or surface. This is the action for "show as sticks", "show as cartoon", "show surface", etc. NOT for zooming in/out (use set-view for that).',
    inputs: [
        param('style', 'string', 'Rendering style', true, { options: ['wireframe', 'sticks', 'ballandstick', 'spacefill', 'cartoon', 'surface', 'hidden'] }),
    ],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'SetDisplayStyle',
    serverCommand: null,
    jobType: null,
};

export const setHydrogenView = {
    id: 'set-hydrogen-view',
    name: 'Show/Hide Hydrogens',
    category: 'Display',
    description: 'Control which hydrogen atoms are visible: all, polar only, or none.',
    inputs: [
        param('mode', 'string', 'Hydrogen display mode', true, { options: ['all', 'polar', 'none'] }),
    ],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'SetHydrogenView',
    serverCommand: null,
    jobType: null,
};

export const setWaterView = {
    id: 'set-water-view',
    name: 'Show/Hide Waters',
    category: 'Display',
    description: 'Control which water molecules are visible: crystallographic, computed (BMaps simulated), all, or none.',
    inputs: [
        param('mode', 'string', 'Water display mode', true, { options: ['none', 'crystal', 'computed', 'all'] }),
    ],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'SetWaterView',
    serverCommand: null,
    jobType: null,
};

export const showHotspots = {
    id: 'show-hotspots',
    name: 'Show/Hide Fragment Hotspots',
    category: 'Display',
    description: 'Toggle hotspot highlighting from fragment simulations. Hotspots show where fragments bind most favorably.',
    inputs: [
        param('show', 'boolean', 'Show or hide', true, { defaultValue: true }),
    ],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'SetHotspots',
    serverCommand: null,
    jobType: null,
};

export const showBindingSiteSurface = {
    id: 'show-binding-site-surface',
    name: 'Show Binding Site Surface',
    category: 'Display',
    description: 'Display the protein binding site surface colored by desolvation energy (ddGs) or hydrophobicity. Helps identify druggable pockets.',
    inputs: [
        param('mode', 'string', 'Surface coloring mode', true, { options: ['ddgs', 'hydrophobicity', 'off'] }),
    ],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'SetBindingSiteSurface',
    serverCommand: null,
    jobType: null,
};

export const setColorScheme = {
    id: 'set-color-scheme',
    name: 'Change Color Scheme',
    category: 'Display',
    description: 'Change the background and atom color scheme of the 3D viewer.',
    inputs: [
        param('scheme', 'string', 'Color scheme name', true),
    ],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'SetColorScheme',
    serverCommand: null,
    jobType: null,
};

export const resetView = {
    id: 'reset-view',
    name: 'Reset 3D View',
    category: 'Display',
    description: 'Reset the 3D camera to the default orientation, zoom level, and display settings.',
    inputs: [],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'ResetView',
    serverCommand: null,
    jobType: null,
};

export const recenterView = {
    id: 'recenter-view',
    name: 'Recenter View',
    category: 'Display',
    description: 'Recenter the 3D view on the visible atoms without changing the display style.',
    inputs: [],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'RedrawScene',
    serverCommand: null,
    jobType: null,
};

// ─── Compound Navigation ───────────────────────────────────────────────────

export const activateCompound = {
    id: 'activate-compound',
    name: 'Focus on Compound',
    category: 'Compound Management',
    description: 'Set a specific compound as the active/focus compound. The 3D viewer and energy displays update to show this compound. Use compound index (1-based) to identify which one.',
    inputs: [
        param('compound', 'Compound', 'Compound to activate'),
    ],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [
        pre('compound_loaded', 'At least one compound must be loaded'),
    ],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'ActivateCompound',
    serverCommand: null,
    jobType: null,
};

export const nextCompound = {
    id: 'next-compound',
    name: 'Next Compound',
    category: 'Compound Management',
    description: 'Navigate to the next compound in the workspace. Cycles through loaded compounds.',
    inputs: [],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [
        pre('compound_loaded', 'At least one compound must be loaded'),
    ],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'ActivateNextCompound',
    serverCommand: null,
    jobType: null,
};

export const toggleCompoundVisibility = {
    id: 'toggle-compound-visibility',
    name: 'Show/Hide Compound',
    category: 'Compound Management',
    description: 'Toggle whether a compound is visible in the 3D viewer. Hidden compounds are still loaded but not displayed.',
    inputs: [
        param('compound', 'Compound', 'Compound to show or hide'),
    ],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'ToggleVisibility',
    serverCommand: null,
    jobType: null,
};

// ─── Dialogs & Panels ──────────────────────────────────────────────────────

export const openSketcher = {
    id: 'open-sketcher',
    name: 'Open Molecular Sketcher',
    category: 'Compound Management',
    description: 'Open the 2D molecular sketcher to draw a new compound or edit the active compound. Drawn compounds can be loaded into the workspace for docking and scoring.',
    inputs: [],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'OpenSketcher',
    serverCommand: null,
    jobType: null,
};

export const openDockingDialog = {
    id: 'open-docking-dialog',
    name: 'Open Docking Dialog',
    category: 'Simulation & Computation',
    description: 'Open the docking configuration dialog where you can set up and submit docking jobs with full control over parameters, box settings, and program selection.',
    inputs: [],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [
        pre('protein_loaded', 'A protein must be loaded'),
    ],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'OpenDock',
    serverCommand: null,
    jobType: null,
};

export const openImportDialog = {
    id: 'open-import-dialog',
    name: 'Open Compound Import',
    category: 'Compound Management',
    description: 'Open the compound import dialog to load molecules from SMILES, SDF, MOL, PDB files, or the molecular sketcher.',
    inputs: [],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'OpenImport',
    serverCommand: null,
    jobType: null,
};

export const openProteinImportDialog = {
    id: 'open-protein-import',
    name: 'Open Protein Import',
    category: 'Target Setup',
    description: 'Open the protein import dialog to load structures from PDB, AlphaFold, file upload, or protein folding services.',
    inputs: [],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'OpenProteinImport',
    serverCommand: null,
    jobType: null,
};

export const openExportDialog = {
    id: 'open-export-dialog',
    name: 'Open Export Dialog',
    category: 'Export & Reporting',
    description: 'Open the export dialog to save compounds, selections, or structures to various file formats (SDF, MOL, PDB, CSV) or external services.',
    inputs: [],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'OpenExport',
    serverCommand: null,
    jobType: null,
};

export const openFragmentManager = {
    id: 'open-fragment-manager',
    name: 'Open Fragment Manager',
    category: 'Fragment Operations',
    description: 'Open the fragment library manager to browse, load, and manage fragment simulation data.',
    inputs: [],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'OpenFragmentManager',
    serverCommand: null,
    jobType: null,
};

// ─── Edit Operations ───────────────────────────────────────────────────────

export const undoAction = {
    id: 'undo',
    name: 'Undo',
    category: 'Workspace Management',
    description: 'Undo the most recent action.',
    inputs: [],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'Undo',
    serverCommand: null,
    jobType: null,
};

export const redoAction = {
    id: 'redo',
    name: 'Redo',
    category: 'Workspace Management',
    description: 'Redo the most recently undone action.',
    inputs: [],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'Redo',
    serverCommand: null,
    jobType: null,
};

// ─── Workspace ─────────────────────────────────────────────────────────────

export const removeProtein = {
    id: 'remove-protein',
    name: 'Remove Protein',
    category: 'Target Setup',
    description: 'Remove a loaded protein structure from the workspace.',
    inputs: [
        param('caseData', 'CaseData', 'Protein case to remove'),
    ],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [
        pre('protein_loaded', 'A protein must be loaded'),
    ],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'RemoveCase',
    serverCommand: null,
    jobType: null,
};

// ─── Additional Display ────────────────────────────────────────────────────

export const showBfactor = {
    id: 'show-bfactor',
    name: 'Show B-factor / Confidence Coloring',
    category: 'Display',
    description: 'Color protein by crystallographic B-factor or AlphaFold pLDDT confidence score. Useful for assessing structural reliability.',
    inputs: [
        param('show', 'boolean', 'Show or hide', true, { defaultValue: true }),
    ],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [pre('protein_loaded', 'A protein must be loaded')],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'ShowBfactor',
    serverCommand: null,
    jobType: null,
};

export const showFragmentPane = {
    id: 'show-fragment-pane',
    name: 'Open Fragment/Water Simulation Panel',
    category: 'Display',
    description: 'Open the fragment or water simulation panel to start a new simulation job. Options: fragment map, water map, or clustering/hotspot simulation.',
    inputs: [
        param('type', 'string', 'Panel type', true, { options: ['fragment', 'water', 'clustering'] }),
    ],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [pre('protein_loaded', 'A protein must be loaded')],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'ShowFragmentPane',
    serverCommand: null,
    jobType: null,
};

export const changeHotspotThreshold = {
    id: 'change-hotspot-threshold',
    name: 'Adjust Hotspot Display Threshold',
    category: 'Display',
    description: 'Change the energy threshold for hotspot visibility. Lower threshold shows only the strongest hotspots; higher threshold shows more.',
    inputs: [
        param('threshold', 'number', 'Energy threshold value'),
    ],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'ChangeHotspotThreshold',
    serverCommand: null,
    jobType: null,
};

// ─── Atom Selection ────────────────────────────────────────────────────────

export const selectAtom = {
    id: 'select-atom',
    name: 'Select Atom',
    category: 'Display',
    description: 'Select a specific atom in the 3D viewer. Selected atoms are used as reference points for fragment growing, search-near, and other operations.',
    inputs: [
        param('atom', 'Atom', 'Atom to select (or null to clear selection)', false),
    ],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'SelectAtom',
    serverCommand: null,
    jobType: null,
};

// ─── Compound Organization ─────────────────────────────────────────────────

export const starCompound = {
    id: 'star-compound',
    name: 'Star/Favorite Compound',
    category: 'Compound Management',
    description: 'Mark a compound as starred/favorited for quick reference. Starred compounds are highlighted in the compound list.',
    inputs: [
        param('compound', 'Compound', 'Compound to star'),
    ],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [pre('compound_loaded', 'A compound must be loaded')],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'ToggleStarred',
    serverCommand: null,
    jobType: null,
};

export const createCompoundGroup = {
    id: 'create-compound-group',
    name: 'Create Compound Group',
    category: 'Compound Management',
    description: 'Create a named group/folder to organize compounds in the workspace. Useful for separating hits from leads, or grouping by series.',
    inputs: [],
    outputs: [],
    parameters: [
        param('name', 'string', 'Group name', false),
    ],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'GroupCreate',
    serverCommand: null,
    jobType: null,
};

export const renameCompoundGroup = {
    id: 'rename-compound-group',
    name: 'Rename Compound Group',
    category: 'Compound Management',
    description: 'Rename an existing compound group.',
    inputs: [
        param('group', 'object', 'Group to rename'),
        param('newName', 'string', 'New name'),
    ],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'GroupRename',
    serverCommand: null,
    jobType: null,
};

export const deleteCompoundGroup = {
    id: 'delete-compound-group',
    name: 'Delete Compound Group',
    category: 'Compound Management',
    description: 'Delete a compound group. Compounds in the group are kept in the workspace but ungrouped.',
    inputs: [
        param('group', 'object', 'Group to delete'),
    ],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'GroupDelete',
    serverCommand: null,
    jobType: null,
};

// ─── Protein Browser ───────────────────────────────────────────────────────

export const openProteinBrowser = {
    id: 'open-protein-browser',
    name: 'Browse Available Proteins',
    category: 'Target Setup',
    description: 'Open the protein map selector to browse and search the full library of available protein structures on the BMaps server.',
    inputs: [],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'OpenMapSelector',
    serverCommand: null,
    jobType: null,
};

// ─── Fragment Operations (additional) ──────────────────────────────────────

export const replaceWithFragmentSet = {
    id: 'replace-with-fragment-set',
    name: 'Replace with Fragment Set',
    category: 'Fragment Operations',
    description: 'Replace a terminal group on the compound with all fragments from a specified fragment set. Produces multiple new compounds, one per fragment replacement.',
    inputs: [
        param('fromAtom', 'Atom', 'Atom at the replacement site'),
        param('toAtom', 'Atom', 'Terminal atom to replace'),
        param('fragsetName', 'string', 'Fragment set name'),
    ],
    outputs: [
        param('compounds', 'CompoundList', 'New compounds with replacements'),
    ],
    parameters: [],
    dependsOn: [],
    preconditions: [
        pre('compound_loaded', 'A compound must be loaded'),
        pre('fragment_map_available', 'Fragment map must be available'),
    ],
    postconditions: [],
    runtime: 'long',
    // Flipped from 'implemented' → 'planned' 2026-05-14. The bound handler
    // (ModificationCmds.replaceWithFragmentSet) explicitly surfaces a
    // "Not Yet Implemented!" alert and returns empty compounds — it's a
    // server-side stub. Leave the ActionDef in the registry so the LLM
    // and the composability graph can plan with it, but the validator
    // should treat it as planned and not auto-dispatch.
    status: 'planned',
    userAction: 'ReplaceWithFragmentSet',
    serverCommand: null,
    jobType: null,
};

// ─── Default Export ────────────────────────────────────────────────────────

export default [
    // Display settings
    setDisplayStyle,
    setHydrogenView,
    setWaterView,
    showHotspots,
    showBindingSiteSurface,
    setColorScheme,
    resetView,
    recenterView,
    showBfactor,
    showFragmentPane,
    changeHotspotThreshold,
    // Atom selection
    selectAtom,
    // Compound navigation
    activateCompound,
    nextCompound,
    toggleCompoundVisibility,
    starCompound,
    // Compound organization
    createCompoundGroup,
    renameCompoundGroup,
    deleteCompoundGroup,
    // Dialogs & panels
    openSketcher,
    openDockingDialog,
    openImportDialog,
    openProteinImportDialog,
    openExportDialog,
    openFragmentManager,
    openProteinBrowser,
    // Edit
    undoAction,
    redoAction,
    // Workspace
    removeProtein,
    // Fragment operations
    replaceWithFragmentSet,
];
